import { Component, ViewChild } from '@angular/core';
import { DeprecatedConfirmationDialogComponent, DeprecatedModalFormComponent } from '@app/components';
import { AssetCategory } from '@app/models/company/asset-category.model';
import { IndexView } from '@app/modules/common/views';
import { AssetCategoriesIndexTable, CompanyMenuService } from '@app/modules/company/services';
import { AssetCategoryForm } from '@app/modules/forms/company/components';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { NotifyService } from '@app/services';
import { TranslatableKey } from '@app/types/translatable.type';

@Component({
    templateUrl: './asset-categories-index.template.html',
    styleUrls: ['./asset-categories-index.style.scss'],
})
export class AssetsCategoriesIndexView extends IndexView {
    @ViewChild('confirmDeleteAssetCategoryDialog', { static: true })
    confirmDeleteAssetCategoryDialog: DeprecatedConfirmationDialogComponent;
    @ViewChild('assetCategoryModal', { static: true }) assetCategoryModal: DeprecatedModalFormComponent;

    isLoading = true;

    currentAssetCategory: AssetCategory = new AssetCategory();
    toDeleteCategory: AssetCategory;

    title: TranslatableKey;

    constructor(
        public table: AssetCategoriesIndexTable,
        private notify: NotifyService,
        public companyMenuService: CompanyMenuService
    ) {
        super();
        this.table.onLoading.subscribe((isLoading) => (this.isLoading = isLoading));
        this.table.reload();
    }

    onCreateAssetCategory(): void {
        this.title = 'company.assets.createCategory';
        this.currentAssetCategory = new AssetCategory();
        this.assetCategoryModal.show();
    }

    async onTableMenuEvent(event: MenuColumnEvent): Promise<void> {
        const eventType = event.menuItem.event;
        this.currentAssetCategory = event.data as AssetCategory;

        switch (eventType) {
            case 'edit':
                this.onEdit();
                break;
            case 'delete':
                this.onDelete(this.currentAssetCategory);
                break;
        }
    }

    onEdit(): void {
        this.title = 'company.assets.editCategory';
        this.assetCategoryModal.show();
    }

    saveAssetCategory(assetCategoryForm: AssetCategoryForm): Promise<void> {
        assetCategoryForm.submit();

        if (assetCategoryForm.form.invalid) {
            return;
        }

        this.isLoading = true;
        const assetCategory = assetCategoryForm.assetCategory;
        const successMessage = assetCategory.isPersisted
            ? 'company.assets.assetCategoryUpdated'
            : 'company.assets.assetCategorySaved';

        assetCategory.save().then(() => {
            this.table.reload();
            this.currentAssetCategory = new AssetCategory();
            assetCategoryForm.reset();

            this.notify.success(successMessage);
            this.isLoading = false;
        });
    }

    onDelete(assetCategory: AssetCategory): void {
        this.toDeleteCategory = assetCategory;

        this.confirmDeleteAssetCategoryDialog.show().then((confirm) => {
            if (!confirm) {
                return;
            }

            this.isLoading = true;
            assetCategory
                .delete()
                .then(() => {
                    this.table.reload();
                    this.isLoading = false;
                })
                .catch((err) => {
                    this.notify.error(err.message || 'company.assets.deleteCategoryFailed');
                });
        });
    }

    onCancel(form: AssetCategoryForm): void {
        form.reset();
        this.currentAssetCategory.revert();
    }
}
